/* Deutsche Schreibweise.
 *
 * Betraege kommen als Zeichenkette aus der API und werden hier auch als
 * Zeichenkette formatiert. Der Umweg ueber Number waere bei einem
 * einzelnen Betrag harmlos und bei einer Summe von einigen hundert nicht
 * mehr - 21225.63 ist in einer JavaScript-Zahl nicht darstellbar.
 */

const EUR = new Intl.NumberFormat('de-DE', {
  style: 'currency', currency: 'EUR',
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})
const EUR0 = new Intl.NumberFormat('de-DE', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
})
const ZAHL = new Intl.NumberFormat('de-DE')
const ZAHL2 = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 })

export type Betrag = string | number | null | undefined

/** Fuer die Anzeige. Rechnen soll damit niemand. */
export const zahlwert = (b: Betrag): number =>
  b === null || b === undefined ? 0 : typeof b === 'number' ? b : Number(b)

export const euro = (b: Betrag): string =>
  b === null || b === undefined ? '–' : EUR.format(zahlwert(b))

/** Ohne Nachkommastellen - fuer Achsen und grosse Uebersichten. */
export const euroKurz = (b: Betrag): string => {
  if (b === null || b === undefined) return '–'
  const n = zahlwert(b)
  if (Math.abs(n) >= 1_000_000) return ZAHL2.format(n / 1_000_000) + ' Mio. €'
  if (Math.abs(n) >= 10_000) return ZAHL.format(Math.round(n / 1000)) + ' Tsd. €'
  return EUR0.format(n)
}

export const anzahl = (n: number | null | undefined): string =>
  n === null || n === undefined ? '–' : ZAHL.format(n)

export const menge = (b: Betrag): string =>
  b === null || b === undefined ? '–' : ZAHL2.format(zahlwert(b))

export const prozent = (p: number | null | undefined, stellen = 1): string =>
  p === null || p === undefined ? '–'
    : new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: stellen, maximumFractionDigits: stellen,
      }).format(p) + ' %'

/** Mit Vorzeichen - fuer Veraenderungen. */
export const delta = (p: number | null | undefined, stellen = 1): string =>
  p === null || p === undefined ? '–' : (p > 0 ? '+' : '') + prozent(p, stellen)

const TAG_LANG = new Intl.DateTimeFormat('de-DE',
  { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
const TAG_KURZ = new Intl.DateTimeFormat('de-DE',
  { day: '2-digit', month: '2-digit' })
const TAG_MITTEL = new Intl.DateTimeFormat('de-DE',
  { weekday: 'short', day: '2-digit', month: '2-digit' })

export const datumLang = (s: string | null | undefined): string =>
  s ? TAG_LANG.format(new Date(s + 'T12:00:00')) : '–'
export const datumKurz = (s: string): string => TAG_KURZ.format(new Date(s + 'T12:00:00'))
export const datumMittel = (s: string): string => TAG_MITTEL.format(new Date(s + 'T12:00:00'))

export const WOCHENTAGE = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag',
                           'Freitag', 'Samstag', 'Sonntag']
export const WOCHENTAGE_KURZ = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']

export const stunde = (h: number): string => String(h).padStart(2, '0') + ' Uhr'

/** Datum als JJJJ-MM-TT, wie die API es erwartet. */
export const alsIso = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

export const tageFrueher = (iso: string, tage: number): string => {
  const d = new Date(iso + 'T12:00:00')
  d.setDate(d.getDate() - tage)
  return alsIso(d)
}

/**
 * Wie die Arten einer Zahlart heißen, wenn ein Mensch sie liest.
 *
 * An **einer** Stelle, weil sie an drei Stellen gebraucht werden
 * (Tagesansicht, Einrichtung, Importprotokoll). Zwei Wortlaute für
 * dieselbe Sache sind in diesem Projekt schon einmal 38 Stellen
 * geworden (S-47).
 *
 * `Karte` fasst zusammen, was die Kasse getrennt nennt: „Tap to Pay auf
 * dem iPhone" läuft im Buchungsgerät selbst, „Lightspeed Payments" geht
 * an ein EC-Terminal — bezahlt wird beide Male mit einer Karte. Die
 * beiden Namen bleiben eine Ebene tiefer erhalten, denn ob ein Standort
 * noch ein EC-Terminal braucht, steht genau dort.
 */
export const ZAHLART_ARTEN: Record<string, string> = {
  CASH: 'Bar',
  CARD: 'Karte',
  MEAL: 'Essensgutschein',
  VOUCHER: 'Gutschein',
  DEBTOR: 'Auf Rechnung',
  OTHER: 'Sonstiges',
  NONE: 'Ohne Zahlung',
}

export const zahlartArt = (art: string): string =>
  ZAHLART_ARTEN[art] ?? 'Sonstiges'

/** Kurze Kundennamen, ohne die von der Kasse gelieferten Namen zu ändern. */
const ZAHLART_ANZEIGENAMEN: Record<string, string> = {
  'Tap to Pay auf dem iPhone': 'Tap to Pay',
}

export const zahlartName = (name: string): string =>
  ZAHLART_ANZEIGENAMEN[name] ?? name

/** In welcher Reihenfolge die Arten stehen. Bar zuerst, weil es die
 *  einzige ist, die der Kassenabschluss selbst ausweist und der
 *  Abgleich gegen ihn prüft. */
export const ZAHLART_FOLGE = ['CASH', 'CARD', 'MEAL', 'VOUCHER', 'DEBTOR', 'OTHER', 'NONE']

/** Feste Töne je Art. Fehlt an einem Tag eine Art, dürfen die übrigen
 *  deshalb nicht plötzlich ihre Farbe wechseln. */
export const ZAHLART_FARBEN: Record<string, string> = {
  CASH: 'var(--reihe-1)',
  CARD: 'var(--reihe-2)',
  MEAL: 'var(--reihe-3)',
  VOUCHER: 'var(--reihe-4)',
  DEBTOR: 'var(--reihe-7)',
  OTHER: 'var(--reihe-5)',
  NONE: 'var(--reihe-6)',
}

export const zahlartFarbe = (art: string): string =>
  ZAHLART_FARBEN[art] ?? 'var(--schrift-3)'
